import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect, useMemo } from "react";
import { z } from "zod";
import { formatDocumentMoney } from "@/shared/lib/performance-document-currency";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import type {
  CreateProductionPayoutDTO,
  ProductionCostLineDTO,
} from "../../domain/contracts";
import { useCreateProductionPayout } from "../api/queries";

const LINE_TYPE_LABELS: Record<ProductionCostLineDTO["lineType"], string> = {
  prop: "Prop",
  costume: "ຊຸດ",
  material: "ວັດສະດຸ",
  service: "ບໍລິການ",
  other: "ອື່ນໆ",
};

const CreateProductionPayoutFormSchema = z.object({
  items: z
    .array(
      z.object({
        costLineId: z.string(),
        selected: z.boolean(),
      }),
    )
    .refine((items) => items.some((item) => item.selected), {
      message: "ເລືອກຢ່າງນ້ອຍ 1 ລາຍການ",
      path: ["items"],
    }),
});

type CreateProductionPayoutFormValues = z.infer<
  typeof CreateProductionPayoutFormSchema
>;

type CreateProductionPayoutDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  costLines: ProductionCostLineDTO[];
};

function formatMoney(value: number) {
  return formatDocumentMoney(value, "LAK");
}

export function CreateProductionPayoutDialog({
  open,
  onOpenChange,
  eventId,
  costLines,
}: CreateProductionPayoutDialogProps) {
  const createProductionPayout = useCreateProductionPayout(eventId);
  const availableLines = useMemo(
    () =>
      costLines.filter(
        (row) => row.status === "planned" && !row.linkedPayoutId,
      ),
    [costLines],
  );

  const methods = RHF.useForm<CreateProductionPayoutFormValues>({
    resolver: zodResolver(CreateProductionPayoutFormSchema),
    defaultValues: { items: [] },
  });

  useEffect(() => {
    if (!open) return;
    methods.reset({
      items: availableLines.map((row) => ({
        costLineId: row.id,
        selected: availableLines.length === 1,
      })),
    });
  }, [open, availableLines, methods]);

  const items = methods.watch("items") ?? [];
  const selectedTotal = availableLines
    .filter((row) => {
      const item = items.find((entry) => entry.costLineId === row.id);
      return item?.selected;
    })
    .reduce((sum, row) => sum + row.subtotal, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>ສ້າງໃບເບີກຈ່າຍ Prop / ວັດສະດຸ</DialogTitle>
        </DialogHeader>
        <FormRoot
          methods={methods}
          onSubmit={methods.handleSubmit(async (values) => {
            const payload: CreateProductionPayoutDTO = {
              costLineIds: values.items
                .filter((item) => item.selected)
                .map((item) => item.costLineId),
            };
            await createProductionPayout.mutateAsync(payload);
            onOpenChange(false);
          })}
          className="space-y-4"
        >
          {availableLines.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              ບໍ່ມີລາຍການທີ່ພ້ອມເບີກຈ່າຍ — ເພີ່ມລາຍການໃໝ່ ຫຼື ລໍຖ້າໃບເບີກຈ່າຍເກົ່າທີ່ຍົກເລີກ
            </p>
          ) : (
            <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
              {availableLines.map((row, index) => (
                <div
                  key={row.id}
                  className="rounded-lg border px-3 py-2"
                >
                  <RHF.Controller
                    name={`items.${index}.costLineId`}
                    render={({ field }) => (
                      <input type="hidden" {...field} value={row.id} />
                    )}
                  />
                  <FormCheckboxCard
                    name={`items.${index}.selected`}
                    label={`${LINE_TYPE_LABELS[row.lineType]} — ${row.description}`}
                    description={`${formatMoney(row.subtotal)}${
                      row.vendorName ? ` · ${row.vendorName}` : ""
                    }`}
                  />
                </div>
              ))}
            </div>
          )}
          <p className="text-sm">
            ລວມເລືອກ:{" "}
            <span className="font-semibold tabular-nums">
              {formatMoney(selectedTotal)}
            </span>
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              disabled={
                createProductionPayout.isPending || availableLines.length === 0
              }
            >
              {createProductionPayout.isPending
                ? "ກຳລັງສ້າງ..."
                : "ສ້າງໃບເບີກຈ່າຍ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
